import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  output,
  signal
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatStore } from '@application/chat.store';
import { AuthStore } from '@application/auth.store';
import { ImgUploadPort } from '@domain/ports';
import { Message, MessageStatus, getImageUrl, isImageMessage } from '@domain/models';
import { ToastService } from '../toast/toast.service';
import { getPaymentStatusUi } from '@shared/payment-status-ui';
import { PaymentResultMessageComponent } from './components/payment-result-message/payment-result-message.component';
import { PaymentManualReviewResultComponent } from './components/payment-result-message/payment-result-manual-review/payment-result-manual-review.component';
import { ReportResultMessageComponent } from './components/report-result-message/report-result-message.component';
import { ReportSubmissionModalComponent } from './components/report-submission-modal/report-submission-modal.component';

type ImageBadgeTone = 'pending' | 'ok' | 'warning' | 'rejected' | 'error';

interface ImageBadge {
  tone: ImageBadgeTone;
  label: string;
  title: string;
}

@Component({
  selector: 'app-message-panel',
  imports: [CommonModule, FormsModule, DatePipe, PaymentResultMessageComponent,
    PaymentManualReviewResultComponent, ReportResultMessageComponent, ReportSubmissionModalComponent],
  templateUrl: './message-panel.component.html',
  styleUrl: './message-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessagePanelComponent {
  private static readonly ALLOWED_IMAGE_TYPES = new Set([
    'image/jpeg',
    'image/jpg',
    'image/png'
  ]);
  private chatStore = inject(ChatStore);
  private auth = inject(AuthStore);
  private imgUpload = inject(ImgUploadPort);
  private toast = inject(ToastService);

  back = output<void>();

  @ViewChild('listEl') listEl?: ElementRef<HTMLDivElement>;
  @ViewChild('manualValidationFormRow') manualValidationFormRow?: ElementRef<HTMLDivElement>;

  readonly chat = computed(() => this.chatStore.selectedChat());
  readonly otherUser = computed(() => this.chatStore.otherUserForSelected());
  readonly currentUser = computed(() => this.auth.currentUser());
  readonly messages = computed(() => this.chat()?.messages ?? []);
  readonly chatLoading = computed(() => this.chatStore.chatLoading());
  readonly connection = computed(() => this.chatStore.connection());
  readonly typing = computed(() => this.chatStore.typing());
  readonly online = computed(() => this.chatStore.connection() === 'connected');
  readonly refreshing = signal(false);
  readonly canSend = computed(() => this.chatStore.connection() === 'connected'
    && !this.chatLoading() && !this.refreshing());
  readonly reportModalId = signal<string | null>(null);
  readonly unavailableReports = signal<ReadonlySet<string>>(new Set());
  readonly reportsNeedingRefresh = signal<ReadonlySet<string>>(new Set());
  readonly manualValidationRequest = signal<{
    chatId: string | number;
    messageId: string;
    createdAt: number;
  } | null>(null);
  readonly modalAction = computed(() => this.messages().find(message => message.id === this.reportModalId()));
  readonly presenceLabel = computed(() => {
    if (this.typing()) return 'escribiendo…';
    switch (this.connection()) {
      case 'connected': return 'en línea';
      case 'connecting': return 'conectando…';
      default: return 'desconectado';
    }
  });

  textMsg = signal<string>('');
  readonly mobileDevice = signal(this.detectMobileDevice());
  readonly attachmentMenuOpen = signal(false);
  isImage = isImageMessage;
  imgUrl = getImageUrl;
  private readonly remoteImageReady = signal<Record<string, true>>({});
  private readonly previewUrls = new Map<string, string>();

  private lastCount = 0;

  constructor() {
    effect(() => {
      const count = this.messages().length;
      if (count !== this.lastCount) {
        this.lastCount = count;
        this.scheduleScroll();
      }
    });
  }

  private scheduleScroll(): void {
    requestAnimationFrame(() => {
      const el = this.listEl?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  isMine(userId: string): boolean {
    return userId === this.currentUser()?.id;
  }

  onSend(): void {
    const text = this.textMsg().trim();
    if (!text) return;
    if (this.refreshing() || this.chatLoading()) return;
    if (!this.canSend()) {
      this.toast.error('Sin conexión. El mensaje no se envió. Reintenta cuando vuelva la señal.');
      return;
    }
    this.chatStore.sendMessage(text);
    this.textMsg.set('');
  }

  async refreshChat(): Promise<void> {
    if (this.refreshing() || this.chatLoading()) return;
    this.attachmentMenuOpen.set(false);
    this.refreshing.set(true);
    try {
      if (!await this.chatStore.refreshSelectedChat()) {
        this.toast.error('No pudimos actualizar el chat. Inténtalo nuevamente.');
      } else {
        this.reportsNeedingRefresh.set(new Set());
        this.toast.success('Chat actualizado correctamente.');
      }
    } finally {
      this.refreshing.set(false);
    }
  }

  isReportResult(message: Message): boolean {
    return message.semanticType === 'REPORT_STATUS'
      && !!message.parentMessageId
      && this.messages().some(parent => parent.id === message.parentMessageId
        && parent.semanticType === 'REPORT_STATUS');
  }

  showManualValidationForm(message: Message): void {
    if (!this.isActiveManualValidationMessage(message)) return;
    this.manualValidationRequest.set({
      chatId: message.chatId,
      messageId: this.messageIdentity(message),
      createdAt: Date.now()
    });
    requestAnimationFrame(() => {
      this.manualValidationFormRow?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    });
  }

  isManualValidationFormFor(message: Message): boolean {
    const request = this.manualValidationRequest();
    return request?.chatId === message.chatId
      && request.messageId === this.messageIdentity(message)
      && this.isActiveManualValidationMessage(message);
  }

  private isActiveManualValidationMessage(message: Message): boolean {
    const chat = this.chat();
    if (!chat || chat.id !== message.chatId) return false;

    const messageId = this.messageIdentity(message);
    const currentMessage = this.messages().find(candidate =>
      candidate.chatId === chat.id && this.messageIdentity(candidate) === messageId
    );
    if (!currentMessage) return false;
    return currentMessage.paymentRetryStatus === 'AVAILABLE';
  }

  private messageIdentity(message: Message): string {
    return message.messageId ?? message.id;
  }

  isReportAction(message: Message): boolean {
    return message.semanticType === 'REPORT_STATUS' && !this.isReportResult(message);
  }

  canOpenReport(message: Message): boolean {
    return this.isReportAction(message) && message.reportStatus === 'AVAILABLE'
      && !this.unavailableReports().has(message.id)
      && !this.reportsNeedingRefresh().has(message.id);
  }

  openReport(message: Message): void {
    if (!this.canOpenReport(message)) return;
    this.reportModalId.set(message.id);
  }

  closeReport(): void {
    this.reportModalId.set(null);
  }

  markReportUnavailable(id: string): void {
    this.unavailableReports.update(current => new Set([...current, id]));
  }

  markReportRefreshRequired(id: string): void {
    this.reportsNeedingRefresh.update(current => new Set([...current, id]));
  }

  onAttachmentClick(fileInput: HTMLInputElement): void {
    if (this.refreshing() || this.chatLoading()) return;
    if (!this.mobileDevice()) {
      fileInput.click();
      return;
    }
    this.attachmentMenuOpen.update(open => !open);
  }

  openAttachmentInput(input: HTMLInputElement): void {
    if (this.refreshing() || this.chatLoading()) return;
    this.attachmentMenuOpen.set(false);
    input.click();
  }

  static readonly MAX_IMAGES = 30;

  async onFileSelected(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    if (this.refreshing() || this.chatLoading()) {
      input.value = '';
      return;
    }
    const files = input.files;
    if (!files || files.length === 0) {
      input.value = '';
      return;
    }
    if (files.length > MessagePanelComponent.MAX_IMAGES) {
      this.toast.error(
        `Máximo ${MessagePanelComponent.MAX_IMAGES} imágenes por vez. Seleccionaste ${files.length}.`
      );
      input.value = '';
      return;
    }
    const selection = Array.from(files);
    if (selection.some(file => !MessagePanelComponent.ALLOWED_IMAGE_TYPES.has(file.type))) {
      this.toast.error('Solo se permiten imágenes JPG, JPEG o PNG.');
      input.value = '';
      return;
    }
    for (const file of selection) {
      void this.queueImageUpload(file);
    }
    input.value = '';
  }

  openImage(message: Message): void {
    const url = this.displayImageUrl(message);
    if (url) window.open(url, '_blank');
  }

  onImageKeydown(event: KeyboardEvent, message: Message): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.openImage(message);
  }

  shouldShowDaySeparator(index: number): boolean {
    const list = this.messages();
    if (index === 0) return list.length > 0;
    return !MessagePanelComponent.sameDay(
      list[index - 1].createdAt ?? 0,
      list[index].createdAt ?? 0
    );
  }

  daySeparatorLabel(ts: number | undefined): string {
    const d = new Date(ts ?? 0);
    const today = new Date();
    if (MessagePanelComponent.sameDay(d.getTime(), today.getTime())) return 'Hoy';
    const yest = new Date(today);
    yest.setDate(today.getDate() - 1);
    if (MessagePanelComponent.sameDay(d.getTime(), yest.getTime())) return 'Ayer';
    return d.toLocaleDateString('es-PE', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  }

  private static sameDay(a: number, b: number): boolean {
    const da = new Date(a);
    const db = new Date(b);
    return da.getFullYear() === db.getFullYear()
        && da.getMonth() === db.getMonth()
        && da.getDate() === db.getDate();
  }

  badgeFor(message: Message): ImageBadge | null {
    if (!isImageMessage(message)) return null;
    if (this.isPaymentResultMessage(message)) return null;
    if (this.isUploadingImage(message)) {
      return { tone: 'pending', label: 'Subiendo', title: 'Subiendo' };
    }
    const paymentBadge = this.paymentBadgeFor(message);
    if (paymentBadge) return paymentBadge;
    switch (message.validation) {
      case 'PENDING':
        return { tone: 'pending', label: 'Validando', title: 'Validando' };
      case 'OK':
        return { tone: 'ok', label: 'Validada', title: 'Validada' };
      case 'REJECTED':
        return {
          tone: 'rejected',
          label: 'Rechazada',
          title: message.validationReason || 'Rechazada'
        };
      case 'ERROR':
        return {
          tone: 'error',
          label: 'Error',
          title: message.validationReason || 'Error'
        };
      default: return null;
    }
  }

  receiptStatusFor(message: Message): 'sent' | 'processing' | 'done' | 'failed' | null {
    if (!this.isMine(message.userId)) return null;
    switch (message.status) {
      case 'sent':
      case 'processing':
      case 'done':
      case 'failed':
        return message.status;
      default:
        return null;
    }
  }

  showSingleCheck(message: Message): boolean {
    return this.receiptStatusFor(message) === 'sent';
  }

  showDoubleCheck(message: Message): boolean {
    const status = this.receiptStatusFor(message);
    return status === 'processing' || status === 'done';
  }

  receiptTitle(message: Message): string | null {
    const status = this.receiptStatusFor(message);
    return status ? this.statusLabel(status) : null;
  }

  private statusLabel(status: Exclude<MessageStatus, 'sending'>): string {
    switch (status) {
      case 'sent':
        return 'Recibido';
      case 'processing':
        return 'Procesando';
      case 'done':
        return 'Completado';
      case 'failed':
        return 'Error al enviar';
    }
  }

  private paymentBadgeFor(message: Message): ImageBadge | null {
    if (!message.paymentStatus) return null;
    const ui = getPaymentStatusUi(message.paymentStatus);
    return {
      tone: ui.tone,
      label: ui.label,
      title: ui.label
    };
  }

  isPaymentResultMessage(message: Message): boolean {
    return message.kind === 'IMAGE' && message.semanticType === 'PAYMENT_STATUS';
  }

  requestScroll(): void {
    this.scheduleScroll();
  }

  goBack(): void { this.back.emit(); }

  isUploadingImage(message: Message): boolean {
    return isImageMessage(message)
      && message.status === 'sending'
      && this.imgUrl(message).startsWith('blob:');
  }

  displayImageUrl(message: Message): string {
    if (message.clientMessageId && message.remoteMediaUrl && this.remoteImageReady()[message.clientMessageId]) {
      return message.remoteMediaUrl;
    }
    return message.localPreviewUrl || this.imgUrl(message);
  }

  shouldPreloadRemoteImage(message: Message): boolean {
    return !!message.clientMessageId
      && !!message.remoteMediaUrl
      && !this.remoteImageReady()[message.clientMessageId];
  }

  markRemoteImageReady(message: Message): void {
    if (!message.clientMessageId) return;
    const previewUrl = this.previewUrls.get(message.clientMessageId);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      this.previewUrls.delete(message.clientMessageId);
    }
    this.remoteImageReady.update(current => ({ ...current, [message.clientMessageId!]: true }));
  }

  private async queueImageUpload(file: File): Promise<void> {
    const clientMessageId = this.generateClientMessageId();
    const previewUrl = URL.createObjectURL(file);
    this.previewUrls.set(clientMessageId, previewUrl);
    this.chatStore.addLocalImagePreview(clientMessageId, previewUrl);

    try {
      const upload = await this.imgUpload.uploadFile(file);
      await this.chatStore.completeImageUpload(clientMessageId, upload);
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      this.previewUrls.delete(clientMessageId);
      console.error('Image upload failed', err);
      this.chatStore.markImageUploadFailed(
        clientMessageId,
        'No se pudo subir la imagen. Intenta nuevamente.'
      );
      this.toast.error('No se pudo subir la imagen. Intenta nuevamente.');
    }
  }

  private generateClientMessageId(): string {
    try {
      return crypto.randomUUID();
    } catch (error) {
      console.error('Unable to generate image client message id', error);
      throw error;
    }
  }

  private detectMobileDevice(): boolean {
    const userAgent = navigator.userAgent;
    const mobileUserAgent = /Android|iPhone|iPad|iPod/i.test(userAgent);
    const ipadDesktopMode = navigator.platform === 'MacIntel'
      && navigator.maxTouchPoints > 1;
    return mobileUserAgent || ipadDesktopMode;
  }
}
